// Shared validation rules for both CreateNews and the edit form in ListNews,
// so "create" and "edit" enforce exactly the same real constraints instead of
// just checking a field is non-empty.
//
// Post-i18n API: each news item carries a `translations` array (one entry per
// locale) instead of flat title/header/text fields. English is required;
// Arabic is optional, but if any Arabic field is filled in, the whole Arabic
// translation must be complete.
import { REQUIRED_LOCALE_CODE } from "utils/translations";

export const MIN_TITLE_LENGTH = 4;
export const MIN_HEADER_LENGTH = 4;
export const MIN_TEXT_WORDS = 4;

// `text` is rich-text HTML (see RichTextEditor) — strip tags before counting
// words, otherwise markup like <p> or <strong> would inflate the count and
// let a near-empty article pass the minimum-length check.
export const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ");

const countWords = (value) => stripHtml(value).trim().split(/\s+/).filter(Boolean).length;

// An editor can produce "<p></p>" (or similar empty markup) for a blank
// editor, which is truthy as a string but has no real content.
const hasText = (value) => stripHtml(value).trim().length > 0;

const isValidUrl = (value) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

// Validates a single locale's translation fields. `required` = true for the
// mandatory locale (English); for optional locales, only enforced if the
// author has started filling it in. `relaxLength` skips the min-length/
// min-word checks (still requires non-empty) — used for source="telegram"
// articles, which are deliberately short breaking-news blurbs published by
// Bot/telegramInput.js outside the dashboard's own create form, so the
// length minimums built for full editorial articles don't apply to them.
export function validateTranslation({ title, header, text, slug }, required, t, relaxLength = false) {
  const errors = {};
  const touched = (title && title.trim()) || (header && header.trim()) || hasText(text);
  if (!required && !touched) return errors;

  if (!title || !title.trim()) {
    errors.title = t("Title is required");
  } else if (!relaxLength && title.trim().length < MIN_TITLE_LENGTH) {
    errors.title = t("Title must be at least {{count}} characters", { count: MIN_TITLE_LENGTH });
  }

  if (!relaxLength && header && header.trim() && header.trim().length < MIN_HEADER_LENGTH) {
    errors.header = t("Header must be at least {{count}} characters", { count: MIN_HEADER_LENGTH });
  }

  if (!hasText(text)) {
    errors.text = t("Text is required");
  } else if (!relaxLength && countWords(text) < MIN_TEXT_WORDS) {
    errors.text = t("Text must be at least {{count}} words (a real article body, not a couple of words)", { count: MIN_TEXT_WORDS });
  }

  return errors;
}

// `locales` is the dashboard's active-languages list (see useLocales() in
// utils/translations.js) — defaults to en/ar so callers that haven't been
// updated to pass it yet keep working the same as before. `source` is the
// News item's origin ("dashboard" | "telegram", see Bot/telegramInput.js on
// the server) — telegram-sourced items get relaxed length checks.
export function validateNewsForm({ categoryId, translations, status, publisherBy, locales, source }, t) {
  const errors = {};

  if (!categoryId) {
    errors.categoryId = t("Category is required");
  }

  const relaxLength = source === "telegram";
  const localeList = locales && locales.length > 0 ? locales : [{ code: "en" }, { code: "ar" }];
  localeList.forEach(({ code }) => {
    const required = code === REQUIRED_LOCALE_CODE;
    const localeErrors = validateTranslation(translations[code] || {}, required, t, relaxLength);
    if (Object.keys(localeErrors).length > 0) {
      errors[code] = localeErrors;
    }
  });

  if (status === "PUBLISHED" && (!publisherBy || !publisherBy.trim())) {
    errors.publisherBy = t("Publisher name is required when status is Published");
  }

  return errors;
}

// Per-locale errors render inside that locale's TabPanel — if the active tab
// is "en" but the only error is on "ar", the user sees nothing wrong and the
// failed submit looks silent. Callers should switch to this tab index (if
// found) whenever validateNewsForm() returns a non-empty errors object.
export function findFirstErrorLocaleIndex(errors, locales) {
  return locales.findIndex(({ code }) => errors[code]);
}

// Short toast description summarizing a validateNewsForm() error object —
// the individual messages already render inline next to each field, so this
// just tells the user where to look rather than repeating every message.
export function summarizeFormErrors(errors, locales, t) {
  const parts = [];
  const localeLabels = locales.filter(({ code }) => errors[code]).map(({ label }) => label);
  if (localeLabels.length > 0) {
    parts.push(t("Check the {{locales}} tab", { locales: localeLabels.join(", ") }));
  }
  if (errors.categoryId) parts.push(errors.categoryId);
  if (errors.publisherBy) parts.push(errors.publisherBy);
  return parts.join(" ");
}

export { isValidUrl };
